import React, { useState, useEffect, useCallback } from 'react';
import { X, FileText } from 'lucide-react';
import PageHero from '../components/common/PageHero';

// Import all certificate images
import cert1 from '../assets/images/port licenses.jpg';
import cert2 from '../assets/images/certificate2.png';
import cert3 from '../assets/images/certificate3.png';
import cert4 from '../assets/images/certificate4.png';
import cert5 from '../assets/images/certificate5.png';
import cert6 from '../assets/images/certificate6.png';
import cert7 from '../assets/images/certificate7.png';

// Import PDFs
import gstPDF from '../assets/pdf/gst.pdf';
import udyamPDF from '../assets/pdf/Udyam Registration Certificate.pdf';

const certificates = [
  { 
    id: 1, 
    image: cert2, 
    title: 'FSSAI License',
    issuer: 'Food Safety and Standards Authority of India',
    description: 'Ensuring safe and nutritious food supply chain compliance.',
    type: 'image'
  },
  { 
    id: 2, 
    image: cert3, 
    title: 'GST Registration',
    issuer: 'Government of India',
    description: 'Registered under Goods and Services Tax for seamless business operations.',
    type: 'pdf',
    pdfUrl: gstPDF
  },
  { 
    id: 3, 
    image: cert4, 
    title: 'IAF Certification',
    issuer: 'International Accreditation Forum',
    description: 'Internationally recognized accreditation for quality management and conformity assessment.',
    type: 'image'
  },
  { 
    id: 4, 
    image: cert5, 
    title: 'ISO 9001:2015 Certification',
    issuer: 'International Organization for Standardization',
    description: 'Certified for quality management systems and operational excellence.',
    type: 'image'
  },
  { 
    id: 5, 
    image: cert6, 
    title: 'MSME Registration',
    issuer: 'Ministry of MSME, Government of India',
    description: 'Registered as a Micro, Small & Medium Enterprise.',
    type: 'image'
  },
  { 
    id: 6, 
    image: cert1, 
    title: 'Mumbai Port Authority License',
    issuer: 'Mumbai Port Authority (Est. 1873)',
    description: 'Authorized to operate at India\'s premier port.',
    type: 'image'
  },
  { 
    id: 7, 
    image: cert3,
    title: 'Udyam Registration Certificate',
    issuer: 'Ministry of MSME, Government of India',
    description: 'Registered under Udyam for Micro, Small & Medium Enterprises.',
    type: 'pdf',
    pdfUrl: udyamPDF
  }
];

const Certifications = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const openModal = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  }, []);

  const handlePDFClick = (pdfUrl, title) => {
    window.open(pdfUrl, '_blank');
  };

  useEffect(() => {
    if (!selectedImage) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedImage, closeModal]);

  return (
    <div className="bg-white min-h-screen pb-16 md:pb-20">
      <PageHero
        title="Our Certifications"
        subtitle="Recognized for our commitment to quality, safety, and excellence in maritime and logistics services."
      />
      <div className="container-custom pt-14 md:pt-16">
        {/* Certifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              className="card-surface bg-lightGray/60 overflow-hidden border-lightGray group flex flex-col"
            >
              {/* Image/PDF Container */}
              <div 
                className="bg-white p-6 flex items-center justify-center h-56 cursor-pointer focus-within:ring-2 focus-within:ring-teal focus-within:ring-inset"
                onClick={() => cert.type === 'pdf' ? handlePDFClick(cert.pdfUrl, cert.title) : openModal(cert.image)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    cert.type === 'pdf' ? handlePDFClick(cert.pdfUrl, cert.title) : openModal(cert.image);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`${cert.type === 'pdf' ? 'Open' : 'View'} ${cert.title} ${cert.type === 'pdf' ? 'PDF' : 'certificate'} ${cert.type === 'pdf' ? 'in new tab' : 'larger'}`}
              >
                {cert.type === 'pdf' ? (
                  <div className="flex flex-col items-center justify-center gap-4 text-center group-hover:scale-105 transition-transform duration-300">
                    <FileText size={64} className="text-teal" />
                    <span className="text-sm text-navy font-medium">Click to open PDF</span>
                  </div>
                ) : (
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                )}
              </div>
              
              {/* Certificate Content */}
              <div className="p-5 lg:p-6 border-t border-slate-200/90 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-navy mb-1 tracking-tight">
                  {cert.title}
                </h3>
                <p className="text-teal text-sm font-medium mb-2">
                  {cert.issuer}
                </p>
                <p className="text-darkGray/70 text-sm leading-relaxed">
                  {cert.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for Full Size Image */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          role="presentation"
        >
          <div className="relative max-w-3xl max-h-[80vh] w-full h-full flex items-center justify-center pointer-events-none">
            <button
              type="button"
              onClick={closeModal}
              className="pointer-events-auto absolute -top-12 right-0 text-white hover:text-teal transition-colors z-10 rounded-md p-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              aria-label="Close certificate view"
            >
              <X size={32} aria-hidden />
            </button>
            <div
              className="pointer-events-auto bg-white rounded-xl p-4 max-w-full max-h-full overflow-auto shadow-card-hover border border-slate-200"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <img
                src={selectedImage}
                alt="Certificate full size"
                className="max-w-full max-h-[70vh] w-auto h-auto object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certifications;